import useIndexedDB from "@/lib/useIndexedDB";
import {
  DocumentArrowUpIcon,
  GlobeAltIcon,
  LifebuoyIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { CircleStackIcon } from "@heroicons/react/24/solid";
import UndrawDecide from "@/components/svg/UndrawDecide";
import { VideoKeys, videos } from "@/lib/resources";
import { classNames, isStudent } from "@/lib/util";
import { useSearchParams } from "next/navigation";
import { useContext, useEffect, useMemo, useState } from "react";
import useSound from "use-sound";
import { CurrentUser, SoundContext, UserContext } from "../Page";
import { FullPageBanner } from "../styles/Layout";
import Tooltip from "../styles/Tooltip";
import ExploreIcon from "../svg/ExploreIcon";
import UndrawSecurity from "../svg/UndrawSecurity";
import CloneRecipe from "./CloneRecipe";
import CreateRecipeManually from "./CreateRecipeManually";
import ErrorBoundary from "./ErrorBoundary";
import FetchRecipe from "./FetchRecipe";
import SearchRecipeWebsites from "./SearchRecipeWebsites";
import UploadRecipeFile from "./UploadRecipeFile";

const recipeKeys: VideoKeys[] = [
  "sharing-discovering-recipes",
  "upload-recipes",
  "adding-recipe-manually",
  "troubleshooting",
];
const recipeVideos = recipeKeys.map((key) => videos[key]);

interface CreateRecipeInnerProps {
  user: CurrentUser;
}

const CreateRecipeInner: React.FC<CreateRecipeInnerProps> = ({ user }) => {
  const searchParams = useSearchParams();

  const [supportOpen, setSupportOpen] = useState(false);
  const [videoId, setVideoId] = useState(recipeVideos[0].id);
  const [autoPlay, setAutoPlay] = useState(false);

  const [creationMethod, setCreationMethod] = useIndexedDB(
    "recipe-creation-method",
    1
  );
  // reset the method if it's set in the URL
  useEffect(() => {
    const method = searchParams?.get("method");
    if (method) {
      if (method === "manual") {
        setCreationMethod(1);
      } else if (method === "clone") {
        setCreationMethod(2);
      } else if (method === "fetch") {
        setCreationMethod(3);
      } else if (method === "search-site") {
        setCreationMethod(4);
      } else if (method === "file") {
        setCreationMethod(5);
      }
    }
  }, []);

  const calculateTransformForMethod = useMemo(
    () => (method: number) =>
      method * 3.75 + (2.5 + 3.75) * (method - 1) - 0.75,
    //                       [    a       ]   [         b               ]   [ c ]
    // a => offset of half of initial item (7.5rem / 2 = 3.75rem)
    //                                     {w-30}
    //
    // b => Offset per extra item for flex-gap (2.5rem) plus item width (3.75rem)
    //                                        {gap-10}                { ?? }
    //
    // c => Remove offset of half arrow div width (1.5rem / 2 = 0.75rem) to centre
    //                                            { w-6 }
    []
  );

  const [recipeName, setRecipeName] = useIndexedDB(
    "manualRecipeName",
    "",
    !user.saveRecipeProgress
  );
  const [search, setSearch] = useIndexedDB(
    "cloneRecipeSearch",
    "",
    !user.saveRecipeProgress
  );
  const [URL, setURL] = useIndexedDB(
    "fetchRecipeURL",
    "",
    !user.saveRecipeProgress
  );
  const [query, setQuery] = useIndexedDB(
    "recipeSearchQuery",
    "",
    !user.saveRecipeProgress
  );
  const [uploading, setUploading] = useIndexedDB(
    "uploadRecipeUploading",
    false,
    !user.saveRecipeProgress
  );

  const [showUpload, setShowUpload] = useState(true);

  const soundContext = useContext(SoundContext);
  const [play] = useSound("/static/sounds/success-2.mp3", {
    volume: 0.5,
    soundEnabled: soundContext.enabled,
  });

  const isOnBasicPlan = !(user.school?.plan !== "basic");

  return (
    <div className="flex min-h-[400px] flex-col items-center space-y-6 pb-40 text-center">
      <div className="relative my-5 w-full rounded-xl bg-gradient-to-br from-teal-500 via-teal-500 to-cyan-500 text-white">
        <div className="absolute right-3 top-3 rounded-lg bg-white/25 p-1.5">
          <Tooltip content="Support Resources" placement="left">
            <button className="" onClick={() => setSupportOpen(true)}>
              <LifebuoyIcon className="icon-lg text-amber-500 hover:text-amber-600" />
            </button>
          </Tooltip>
          {/* <MajorModal
            isOpen={supportOpen}
            onClose={() => {
              setSupportOpen(false);
            }}
          >
            <div className="flex justify-between gap-5 lg:flex-col">
              <div className="w-88 shrink-0 xl:w-80 lg:w-full">
                <h1 className="text-4xl font-normal">Recipes</h1>
                <p className="mt-4 text-2xl font-normal text-gray-600">
                  ClassCart makes it easy to manage a growing collection of
                  recipes. You can add something of your own, discover a shared
                  recipe, or find something online.
                </p>
                <p className="mt-10 text-2xl font-normal text-gray-800">
                  Learn more with these short video guides
                  <ArrowDownIcon className="mx-1.5 w-4 animate-bounce text-black" />
                </p>
              </div>
              <iframe
                key={videoId}
                className="aspect-video w-full max-w-6xl flex-1 rounded-xl"
                src={`https://youtube.com/embed/${videoId}${
                  autoPlay ? "?autoplay=1" : ""
                }`}
              ></iframe>
            </div>

            <div className="flex flex-wrap items-stretch gap-10 py-5 lg:flex-col lg:gap-5">
              {recipeVideos.map((video) => (
                <button
                  key={video.id}
                  onClick={() => {
                    setVideoId(video.id);
                    setAutoPlay(true);
                  }}
                  className="group flex max-w-3xl shrink-0 grow basis-140 items-center gap-2.5 rounded-md border border-solid border-gray-200 bg-gray-100 p-2 transition hover:bg-gray-200 lg:max-w-full lg:basis-full"
                >
                  <PlayIcon className="w-18 shrink-0 text-gray-500 transition group-hover:text-red-500" />
                  <div className="max-w-2xl">
                    <p className="text-xl font-normal">
                      {video.title.replace(" | PG Quick Tips", "")}
                    </p>
                    <p>{video.description}</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="mb-16 mt-5 w-max rounded-md bg-gray-50 p-2.5">
              <div className="flex">
                <div className="flex-shrink-0">
                  <QuestionMarkCircleIcon
                    className="w-6 text-gray-600"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-2 flex-1 md:flex md:justify-between">
                  <p className="text-xl font-normal text-gray-700">
                    Is there anything not yet covered that you would like to see
                    covered?
                  </p>
                  <p className="mt-3 text-xl md:ml-3.5 md:mt-0">
                    <button
                      className="whitespace-nowrap font-medium text-gray-700 hover:text-gray-500"
                      onClick={() => {
                        setSupportOpen(false);

                        (window as any).Intercom(
                          "showNewMessage",
                          "Hi, I would like to see a video covering [insert here]"
                        );
                      }}
                    >
                      Let us know <span aria-hidden="true">&rarr;</span>
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </MajorModal> */}

          {user.saveRecipeProgress ? (
            <Tooltip content="Recipe progress will be saved" placement="left">
              <span>
                <CircleStackIcon className="icon-lg cursor-help text-white transition hover:text-white" />
              </span>
            </Tooltip>
          ) : null}
        </div>

        <h1
          className={classNames(
            "mb-8 overflow-hidden text-3xl font-medium transition-all",
            (creationMethod === 1 && recipeName) ||
              (creationMethod === 2 && search) ||
              (creationMethod === 3 && URL) ||
              (creationMethod === 4 && query) ||
              (creationMethod === 5 && uploading)
              ? "h-0 opacity-0"
              : "mt-16 h-18 opacity-100"
          )}
        >
          How will you create your recipe?
        </h1>

        <div className="relative mx-auto flex w-max items-center justify-center gap-10 overflow-hidden">
          <button
            className="group mb-6 flex flex-col items-center gap-2"
            onClick={() => {
              setCreationMethod(1);
            }}
          >
            <div
              className={classNames(
                "flex h-14 w-14 items-center justify-center rounded-full shadow transition group-hover:bg-white group-hover:text-gray-400",
                creationMethod === 1 ? "bg-white text-gray-400" : "bg-white/25"
              )}
            >
              <PencilSquareIcon className="w-8 transition" />
            </div>
            <span className="w-30 font-normal">Create It Manually</span>
          </button>

          <button
            className="group mb-6 flex flex-col items-center gap-2"
            onClick={() => {
              setCreationMethod(2);
            }}
          >
            <div
              className={classNames(
                "flex h-14 w-14 items-center justify-center rounded-full shadow transition group-hover:bg-white group-hover:text-cyan-400",
                creationMethod === 2 ? "bg-white text-cyan-400" : "bg-white/25"
              )}
            >
              <ExploreIcon className="w-8 transition" />
            </div>
            <span className="w-30 font-normal">Discover Shared Recipes</span>
          </button>

          <button
            className={classNames(
              "group mb-6 flex flex-col items-center gap-2",
              isOnBasicPlan ? "cursor-not-allowed opacity-50" : ""
            )}
            onClick={() => {
              setCreationMethod(3);
            }}
            disabled={isOnBasicPlan}
          >
            <div
              className={classNames(
                "flex h-14 w-14 items-center justify-center rounded-full shadow transition",
                !isOnBasicPlan
                  ? "group-hover:bg-white group-hover:text-blue-400"
                  : "",
                creationMethod === 3 ? "bg-white text-blue-400" : "bg-white/25"
              )}
            >
              <GlobeAltIcon className="w-8 transition" />
            </div>
            <span className="w-30 font-normal">Fetch From Website</span>
          </button>

          <button
            className={classNames(
              "group mb-6 flex flex-col items-center gap-2",
              isOnBasicPlan ? "cursor-not-allowed opacity-50" : ""
            )}
            onClick={() => {
              setCreationMethod(4);
            }}
            disabled={isOnBasicPlan}
          >
            <div
              className={classNames(
                "flex h-14 w-14 items-center justify-center rounded-full shadow transition",
                !isOnBasicPlan
                  ? "group-hover:bg-white group-hover:text-amber-400"
                  : "",
                creationMethod === 4 ? "bg-white text-amber-400" : "bg-white/25"
              )}
            >
              <MagnifyingGlassIcon className="w-8 transition" />
            </div>
            <span className="w-30 font-normal">Search Recipe Websites</span>
          </button>

          {showUpload ? (
            <button
              className="group mb-6 flex flex-col items-center gap-2"
              onClick={() => {
                setCreationMethod(5);
              }}
            >
              <div
                className={classNames(
                  "relative flex h-14 w-14 items-center justify-center rounded-full shadow transition group-hover:bg-white group-hover:text-green-600",
                  creationMethod === 5
                    ? "bg-white text-green-600"
                    : "bg-white/25"
                )}
              >
                <DocumentArrowUpIcon className="w-8 transition" />
              </div>
              <span className="w-30 font-normal">Upload From File</span>
            </button>
          ) : null}

          <div
            className="absolute -bottom-5 left-0 h-6 w-6 rotate-45 transform rounded bg-gray-100 transition duration-300 ease-in-out"
            style={{
              transform:
                "translateX(" +
                calculateTransformForMethod(creationMethod) +
                "rem) rotate(45deg)",
            }}
          ></div>
        </div>
      </div>

      {creationMethod === 1 ? (
        <CreateRecipeManually user={user} setRecipeNameFn={setRecipeName} />
      ) : creationMethod === 2 ? (
        <CloneRecipe setSearchFn={setSearch} />
      ) : creationMethod === 3 ? (
        <FetchRecipe setURLFn={setURL} user={user} />
      ) : creationMethod === 4 ? (
        <SearchRecipeWebsites user={user} setQueryFn={setQuery} />
      ) : creationMethod === 5 ? (
        <UploadRecipeFile setUploadingFn={setUploading} user={user} />
      ) : (
        <div className="flex flex-col items-center justify-center py-18">
          <UndrawDecide className="w-88" />
          <div className="mt-10 text-center">
            <h1 className="mb-2">Select a recipe creation method</h1>
          </div>
        </div>
      )}

      {/* <Tooltip content={showUpload ? "Ta Da 🎉" : "Click Me"}>
            <button
              onClick={() => {
                if (!showUpload) {
                  play();
                  toast("File upload unlocked", {
                    icon: <Gift className="w-8 text-amber-500" />,
                  });
                }

                setShowUpload(!showUpload);
              }}
              className="fixed top-1/2 right-0 p-3 opacity-0 transition-all duration-500 hover:opacity-100"
            >
              {showUpload ? (
                <Gift className="w-8 text-amber-500" />
              ) : (
                <QuestionMarkCircleIcon className="w-8" />
              )}
            </button>
          </Tooltip> */}
    </div>
  );
};

interface Props {}

const CreateRecipe: React.FC<Props> = () => {
  return (
    <UserContext.Consumer>
      {(user) =>
        isStudent(user) && !user.school?.permCreateRecipes ? (
          <FullPageBanner>
            <UndrawSecurity className="w-88" />
            <div className="mt-20 text-center">
              <h1 className="mb-2">Sorry. Students cannot create recipes.</h1>
              <p className="">
                To regain access, ask a teacher to change the school settings.
              </p>
            </div>
          </FullPageBanner>
        ) : (
          <ErrorBoundary>
            <CreateRecipeInner user={user} />
          </ErrorBoundary>
        )
      }
    </UserContext.Consumer>
  );
};

export default CreateRecipe;
